import { useCallback, useEffect, useState } from 'react';
import { useAppVersion, reloadToLatest } from './useAppVersion';
import { BrowserRouter, Link, NavLink, Navigate, Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from './api';
import { Spinner, ToastProvider } from './components/ui';
import { KitjaLogo } from './components/KitjaLogo';
import type { Language, Me } from './types';
import { DashboardPage } from './pages/DashboardPage';
import { ShoppingPage } from './pages/ShoppingPage';
import { MealsPage } from './pages/MealsPage';
import { CupboardPage } from './pages/CupboardPage';
import { AdminPage } from './pages/AdminPage';
import { CookPage } from './pages/CookPage';
import { SettingsPage } from './pages/SettingsPage';
import { SignInPage } from './pages/SignInPage';
import { SetupPage } from './pages/SetupPage';

const NAV = [
  { to: '/', en: 'Today', fr: "Aujourd'hui", end: true },
  { to: '/plan', en: 'Plan', fr: 'Planifier' },
  { to: '/shopping', en: 'Shopping', fr: 'Courses' },
  { to: '/cupboard', en: 'Cupboard', fr: 'Placard' },
  { to: '/meals', en: 'Meals', fr: 'Repas' },
];

/**
 * "Have you pushed?" answered by the app itself.
 *
 * A progressive web app keeps serving the bundle it already has, so a change
 * that shipped an hour ago can be invisible for days on an installed tablet.
 * When the server reports a build this page is not, say so and offer the
 * reload — rather than leaving somebody to wonder whether the work was done.
 */
function UpdateBanner({ language }: { language: Language }) {
  const { stale } = useAppVersion();
  if (!stale) return null;

  return (
    <div className="update-banner">
      <span>
        {language === 'fr'
          ? 'Une nouvelle version de Kitja est disponible.'
          : 'A newer version of Kitja is ready.'}
      </span>
      <button className="btn small" onClick={() => void reloadToLatest()}>
        {language === 'fr' ? 'Recharger' : 'Reload'}
      </button>
    </div>
  );
}

/**
 * A way in to the operator's view, for the one person who has one.
 *
 * Asking is cheap and the answer is a boolean; the route itself is guarded on
 * the server, so this only decides whether the door is visible.
 */
function OperatorLink() {
  const [operator, setOperator] = useState(false);

  useEffect(() => {
    void api
      .adminWhoami()
      .then((result) => setOperator(result.operator))
      .catch(() => setOperator(false));
  }, []);

  if (!operator) return null;
  return (
    <NavLink
      to="/admin"
      className={({ isActive }) => (isActive ? 'active' : '')}
      aria-label="Households"
      title="Households"
    >
      ⌂
    </NavLink>
  );
}

function Brand() {
  return (
    <Link className="brand" to="/">
      <KitjaLogo size={34} />
      <span>Kitja</span>
    </Link>
  );
}

function Shell({
  me,
  language,
  setLanguage,
  onSignOut,
}: {
  me: Me;
  language: Language;
  setLanguage: (language: Language) => void;
  onSignOut: () => void;
}) {
  return (
    <div className="app">
      <header className="topbar">
        <Brand />
        <nav className="nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => (isActive ? 'active' : '')}
            >
              {language === 'fr' ? item.fr : item.en}
            </NavLink>
          ))}
          <OperatorLink />
          <NavLink to="/settings" className={({ isActive }) => (isActive ? 'active' : '')} aria-label="Settings">
            ⚙
          </NavLink>
        </nav>
      </header>

      <UpdateBanner language={language} />

      <main className="main">
        <Routes>
          <Route path="/" element={<DashboardPage page="today" language={language} me={me} />} />
          <Route path="/plan" element={<DashboardPage page="plan" language={language} me={me} />} />
          {/* The old address, kept so existing bookmarks and home-screen tiles work. */}
          <Route path="/week" element={<Navigate to="/plan" replace />} />
          <Route path="/shopping" element={<ShoppingPage language={language} />} />
          <Route path="/cupboard" element={<CupboardPage language={language} me={me} />} />
          {/* Guarded on the server too — this only hides the door. */}
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/meals" element={<MealsPage language={language} />} />
          <Route path="/cook/:plannedMealId" element={<CookPage language={language} setLanguage={setLanguage} />} />
          <Route
            path="/settings"
            element={<SettingsPage language={language} setLanguage={setLanguage} me={me} onSignOut={onSignOut} />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

/** Signs this browser in as a permanent household device (§7). */
function DeviceClaim({ onClaimed }: { onClaimed: () => void }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const token = params.get('token');

  useEffect(() => {
    if (!token) {
      setError('No device token in the link.');
      return;
    }
    api
      .claimDevice(token)
      .then(() => {
        onClaimed();
        navigate('/', { replace: true });
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not set up this device'));
  }, [token, navigate, onClaimed]);

  return (
    <div className="main">
      <div className="card">
        <div className="card-body">
          {error ? <p className="banner warn">{error}</p> : <p>Setting up this device…</p>}
        </div>
      </div>
    </div>
  );
}

/** Handles the magic-link callback. */
function AuthCallback({ onSignedIn }: { onSignedIn: () => void }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const token = params.get('token');

  useEffect(() => {
    if (!token) {
      setError('That link is missing its token.');
      return;
    }
    api
      .redeem(token)
      .then(() => {
        onSignedIn();
        navigate('/', { replace: true });
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not sign in'));
  }, [token, navigate, onSignedIn]);

  return (
    <div className="main">
      <div className="card">
        <div className="card-body">
          {error ? (
            <>
              <p className="banner warn">{error}</p>
              <p style={{ marginTop: 14 }}>
                <Link className="btn" to="/">
                  Try again
                </Link>
              </p>
            </>
          ) : (
            <p>Signing you in…</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [language, setLanguageState] = useState<Language>(
    () => (localStorage.getItem('language') as Language) || 'en',
  );

  const setLanguage = useCallback((next: Language) => {
    localStorage.setItem('language', next);
    setLanguageState(next);
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    api
      .me()
      .then((result) => {
        setMe(result);
        if (result.household?.default_language && !localStorage.getItem('language')) {
          setLanguageState(result.household.default_language);
        }
      })
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.status === 401) setMe({ signedIn: false });
        else setMe({ signedIn: false });
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const onSignOut = useCallback(async () => {
    await api.signOut();
    load();
  }, [load]);

  return (
    <BrowserRouter>
      <ToastProvider>
        <Routes>
          <Route path="/device" element={<DeviceClaim onClaimed={load} />} />
          <Route path="/auth/callback" element={<AuthCallback onSignedIn={load} />} />
          <Route
            path="*"
            element={
              loading || !me ? (
                <div className="main" style={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
                  <Spinner />
                </div>
              ) : me.signedIn && me.household && !me.household.setup_complete ? (
                // A household nobody has filled in cannot plan anything, so
                // asking beats showing an empty week and letting them wonder.
                <SetupPage householdName={me.household.name} onDone={load} />
              ) : me.signedIn ? (
                <Shell me={me} language={language} setLanguage={setLanguage} onSignOut={onSignOut} />
              ) : (
                <SignInPage onSignedIn={load} />
              )
            }
          />
        </Routes>
      </ToastProvider>
    </BrowserRouter>
  );
}
